import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { BudgetItem } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { Wallet, Plus, Trash2, Edit2, PlusCircle } from 'lucide-react';
import { aggregateBudget } from '../lib/budget';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

export default function Budget() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [items, setItems] = useState<BudgetItem[]>([]);
  const [totals, setTotals] = useState({ planned: 0, paid: 0, remaining: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<BudgetItem>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchBudget = async () => {
    if (!wedding) return;
    const result = await aggregateBudget(wedding.id);
    setItems(result.items);
    setTotals({
       planned: result.totalPlanned,
       paid: result.totalPaid,
       remaining: result.totalRemaining
    });
  };

  useEffect(() => {
    fetchBudget();
  }, [wedding]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const payload = { ...formData, wedding_id: wedding.id, source_type: 'manual', include_in_budget: true };
    if (formData.id) {
      const { error } = await supabase.from('budget_items').update(payload).eq('id', formData.id);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    } else {
      const { error } = await supabase.from('budget_items').insert(payload);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    }
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    fetchBudget();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('budget_items').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchBudget();
  };

  if (!wedding) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.budget')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-1">{t('budget.subtitle')}</p>
        </div>
        <Button 
          variant="secondary"
          onClick={() => { setFormData({}); setIsModalOpen(true); }} 
          icon={<PlusCircle size={18} />}
        >
          {t('common.add')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <Card>
            <p className="text-ds-meta text-slate-400 mb-2">{t('budget.planned')}</p>
            <h3 className="text-ds-h2 text-slate-900 dark:text-slate-200 font-medium">{totals.planned.toLocaleString()} Kč</h3>
         </Card>
         <Card>
            <p className="text-ds-meta text-slate-400 mb-2">{t('budget.paid')}</p>
            <h3 className="text-ds-h2 text-slate-900 dark:text-green-400 font-medium">{totals.paid.toLocaleString()} Kč</h3>
         </Card>
         <Card>
            <p className="text-ds-meta text-slate-400 mb-2">{t('budget.remaining')}</p>
            <h3 className="text-ds-h2 text-slate-900 dark:text-slate-200 font-medium">{totals.remaining.toLocaleString()} Kč</h3>
         </Card>
      </div>

      <Card className="p-0 overflow-hidden">
         {items.length === 0 ? (
            <div className="p-6">
              <EmptyState icon={Wallet} title={t('budget.empty_title')} description={t('budget.empty_description')} />
            </div>
         ) : (
            <div className="overflow-x-auto">
               <table className="w-full text-left">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                     <tr>
                        <th className="px-6 py-4 text-ds-meta text-slate-500">{t('budget.table_item')}</th>
                        <th className="px-6 py-4 text-ds-meta text-slate-500">{t('budget.table_category')}</th>
                        <th className="px-6 py-4 text-ds-meta text-slate-500 text-right">{t('budget.table_plan')}</th>
                        <th className="px-6 py-4 text-ds-meta text-slate-500 text-right">{t('budget.table_paid')}</th>
                        <th className="px-6 py-4 text-ds-meta text-slate-500 text-right">{t('budget.table_actions')}</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                     {items.map(item => (
                        <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group">
                           <td className="px-6 py-4 text-ds-body text-slate-600 dark:text-slate-400">
                              {item.title}
                              {item.source_type !== 'manual' && (
                                 <span className="ml-2 text-ds-meta text-slate-400">{t('common.linked')}</span>
                              )}
                           </td>
                           <td className="px-6 py-4 text-ds-body text-slate-500">{item.category}</td>
                           <td className="px-6 py-4 text-right text-ds-body text-slate-600 dark:text-slate-400">{item.planned_amount.toLocaleString()}</td>
                           <td className="px-6 py-4 text-right text-ds-body text-slate-600 dark:text-slate-400">{item.paid_amount.toLocaleString()}</td>
                           <td className="px-6 py-4 text-right">
                              {item.source_type === 'manual' && (
                                 <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => { setFormData(item); setIsModalOpen(true); }} className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"><Edit2 size={16} /></button>
                                    <button onClick={() => setDeleteId(item.id)} className="p-2 text-slate-400 hover:text-red-500 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"><Trash2 size={16} /></button>
                                 </div>
                              )}
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         )}
      </Card>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? t('budget.modal_edit') : t('budget.modal_new')}>
         <form onSubmit={handleSave} className="space-y-4">
            <Input 
              label={t('budget.form_item_name')}
              type="text" 
              required 
              value={formData.title || ''} 
              onChange={e => setFormData({ ...formData, title: e.target.value })} 
            />
            <Input 
              label={t('budget.form_category')}
              type="text" 
              required 
              value={formData.category || ''} 
              onChange={e => setFormData({ ...formData, category: e.target.value })} 
            />
            <div className="grid grid-cols-2 gap-4">
               <Input 
                 label={t('budget.form_planned_amount')}
                 type="number" 
                 required 
                 value={formData.planned_amount || 0} 
                 onChange={e => setFormData({ ...formData, planned_amount: parseFloat(e.target.value) })} 
               />
               <Input 
                 label={t('budget.paid')}
                 type="number" 
                 value={formData.paid_amount || 0} 
                 onChange={e => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) })} 
               />
            </div>
            <Button type="submit" className="w-full">{t('common.save')}</Button>
         </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
